/**
 * AI Memo Analyzer — 클라이언트 사이드 Firebase AI Logic
 *
 * Gemini 2.5 Flash-Lite 호출 — 서버(aiGenerateText) 경유. 클라이언트는 모델 API 를 직접 부르지 않는다.
 * - API 키 불필요 (Firebase 프로젝트 인증 자동)
 * - 과금은 Firebase/GCP 프로젝트로 통합
 * - Google AI 백엔드 (최저 가격)
 */

import { getAiTextModel } from './AiTextClient';
import { getApp } from 'firebase/app';
import { AiTag, MinutesSummary } from '../models/AiMemo';

// ---------------------------------------------------------------------------
// Gemini Model Setup
// ---------------------------------------------------------------------------

const MODEL_NAME = 'gemini-2.5-flash-lite';

// 서버 경유 — 브라우저가 Firebase AI Logic 을 직접 부르면 공개된 클라이언트 설정만으로
// 외부인이 우리 프로젝트 명의로 모델을 호출할 수 있다 (2026-08-10 비용 사고). 계약은 동일.
function getModel() {
  return getAiTextModel(MODEL_NAME);
}

// ---------------------------------------------------------------------------
// Category Constants
// ---------------------------------------------------------------------------

const CATEGORIES = [
    { id: 'work', name: '업무' },
    { id: 'people', name: '사람' },
    { id: 'idea', name: '아이디어' },
    { id: 'todo', name: '할 일' },
    { id: 'meeting', name: '회의' },
    { id: 'study', name: '배움' },
    { id: 'daily', name: '일상' },
];

const CATEGORY_IDS = CATEGORIES.map(c => c.id);

// ---------------------------------------------------------------------------
// JSON 파싱 헬퍼
// ---------------------------------------------------------------------------

function parseJSON(text: string): any {
    // 마크다운 코드 블록 제거
    const cleaned = text
        .replace(/^```json?\n?/g, '')
        .replace(/\n?```$/g, '')
        .trim();
    return JSON.parse(cleaned);
}

function langLabel(lang: string): string {
    if (lang.startsWith('ko')) return 'Korean';
    if (lang.startsWith('ja')) return 'Japanese';
    return 'English';
}

// ---------------------------------------------------------------------------
// 1. analyzeMemo — 메모 분류/태그/요약
// ---------------------------------------------------------------------------

export interface MemoAnalysisResult {
    category: string;
    tags: AiTag[];
    summary: string;
    suggestedTitle?: string;
}

export async function analyzeMemo(
    content: string,
    title?: string,
    language?: string,
): Promise<MemoAnalysisResult> {
    const lang = language || 'ko';
    const model = getModel();

    const prompt = `You are an AI assistant that analyzes text memos. Analyze the following memo and return a JSON response.

**Memo content:**
${content}

**Available categories:** ${CATEGORIES.map(c => `${c.id}(${c.name})`).join(', ')}

**Instructions:**
1. Choose the BEST matching category from the list above. If the memo covers multiple topics, pick the most dominant one.
2. Generate 3-7 relevant tags covering ALL topics mentioned in the memo.
3. Summarize each distinct topic separately in 1-2 sentences per topic. Use line breaks between topics.
4. If the title is empty, suggest a short title.

**Response language:** ${langLabel(lang)}

**IMPORTANT:** Return ONLY valid JSON, no markdown code blocks. Use this exact structure:
{
  "category": "category_id",
  "tags": ["tag1", "tag2", "tag3"],
  "summary": "summary covering all topics",
  "suggestedTitle": "title if needed"
}`;

    const result = await model.generateContent(prompt);
    const text = result.response.text();
    const parsed = parseJSON(text);

    // 카테고리 유효성 검증
    if (!CATEGORY_IDS.includes(parsed.category)) {
        parsed.category = 'daily';
    }

    // 태그 배열 구성
    const tags: AiTag[] = (parsed.tags || []).slice(0, 7).map((name: string) => ({
        name: name.trim(),
        isActive: true,
        source: 'ai' as const,
        createdAt: new Date(),
    }));

    return {
        category: parsed.category,
        tags,
        summary: parsed.summary || '',
        suggestedTitle: (!title && parsed.suggestedTitle) ? parsed.suggestedTitle : undefined,
    };
}

// ---------------------------------------------------------------------------
// 2. analyzeMinutes — 회의록 구조화
// ---------------------------------------------------------------------------

export interface MinutesAnalysisResult {
    title: string;
    minutes: MinutesSummary;
    tags: AiTag[];
    icon: string;
}

export async function analyzeMinutes(
    transcript: string,
    language?: string,
    duration?: number,
): Promise<MinutesAnalysisResult> {
    const lang = language || 'ko';
    const model = getModel();
    const totalDuration = duration || 0;

    const prompt = `You are an AI meeting minutes assistant. Analyze the following meeting transcript and create structured meeting minutes.

**Transcript:**
${transcript}

**Instructions:**
1. Extract a short meeting title.
2. Identify the meeting agenda/topic.
3. List attendees if mentioned.
4. Summarize key discussion points (topic + detail). For each discussion, include "timestamp" as the number of seconds from the start when that topic was first discussed. The transcript contains [MM:SS] time markers. Convert them to total seconds: for example [01:30] = 90, [02:15] = 135, [00:45] = 45. If only one timestamp exists, estimate positions proportionally within the recording duration.${totalDuration > 0 ? ` The total recording duration is ${totalDuration} seconds. All timestamps MUST be between 0 and ${totalDuration}. Never exceed ${totalDuration}.` : ''} The timestamp MUST be an integer number, never null or empty.
5. List decisions made.
6. List action items with assignees and deadlines if mentioned.
7. List pending/unresolved items.
8. Generate 3-5 relevant tags.
9. Pick a single emoji icon that best represents the meeting topic. Examples: 💰 for budget, 📊 for data/analytics, 🚀 for launch/release, 🔧 for technical, 👥 for HR/hiring, 📱 for mobile/app, 🎯 for strategy, 🏥 for medical, 📚 for education, 🎨 for design, 🔒 for security, etc.

**Response language:** ${langLabel(lang)}

**IMPORTANT:** Return ONLY valid JSON, no markdown code blocks. Use this exact structure:
{
  "title": "meeting title",
  "icon": "single emoji",
  "minutes": {
    "agenda": "meeting agenda",
    "attendees": ["person1", "person2"],
    "keyDiscussions": [
      {"topic": "topic", "detail": "detail", "timestamp": 0}
    ],
    "decisions": ["decision1"],
    "actionItems": [
      {"task": "task description", "assignee": "person", "deadline": "date"}
    ],
    "pendingItems": ["pending item"]
  },
  "tags": ["tag1", "tag2"]
}`;

    const result = await model.generateContent(prompt);
    const text = result.response.text();
    const parsed = parseJSON(text);

    const tags: AiTag[] = (parsed.tags || []).slice(0, 7).map((name: string) => ({
        name: name.trim(),
        isActive: true,
        createdAt: new Date(),
    }));

    const minutes: MinutesSummary = {
        agenda: parsed.minutes?.agenda || '',
        attendees: parsed.minutes?.attendees || [],
        keyDiscussions: (parsed.minutes?.keyDiscussions || []).map((item: any) => ({
            topic: item.topic || '',
            detail: item.detail || '',
            timestamp: typeof item.timestamp === 'number' ? Math.min(item.timestamp, totalDuration > 0 ? totalDuration : Infinity) : undefined,
        })),
        decisions: parsed.minutes?.decisions || [],
        actionItems: (parsed.minutes?.actionItems || []).map((item: any) => ({
            task: item.task || '',
            assignee: item.assignee || undefined,
            deadline: item.deadline || undefined,
        })),
        pendingItems: parsed.minutes?.pendingItems || [],
    };

    return {
        title: parsed.title || '',
        icon: parsed.icon || '📝',
        minutes,
        tags,
    };
}

// ---------------------------------------------------------------------------
// 3. reanalyzeTags — 태그 diff 생성
// ---------------------------------------------------------------------------

export interface TagReanalysisResult {
    tags: AiTag[];
    category: string;
    summary: string;
}

export async function reanalyzeTags(
    content: string,
    existingTags: AiTag[],
): Promise<TagReanalysisResult> {
    const model = getModel();
    const existingTagNames = existingTags.map(t => t.name);

    const prompt = `You are an AI tag analyzer. A memo has been edited, and you need to determine which tags should be kept, removed, or added.

**Updated memo content:**
${content}

**Existing tags:** ${existingTagNames.join(', ')}

**Instructions:**
1. Analyze the updated content.
2. Decide which existing tags are still relevant (keep).
3. Decide which existing tags are no longer relevant (remove).
4. Suggest new tags if the content has changed significantly.
5. Also re-evaluate the category and update the summary.

**Available categories:** ${CATEGORIES.map(c => `${c.id}(${c.name})`).join(', ')}

**IMPORTANT:** Return ONLY valid JSON, no markdown code blocks:
{
  "keepTags": ["tag1", "tag2"],
  "removeTags": ["old_tag"],
  "newTags": ["new_tag"],
  "category": "category_id",
  "summary": "updated summary"
}`;

    const result = await model.generateContent(prompt);
    const text = result.response.text();
    const parsed = parseJSON(text);

    if (!CATEGORY_IDS.includes(parsed.category)) {
        parsed.category = 'daily';
    }

    const now = new Date();
    const tags: AiTag[] = [];

    // 유지 태그
    for (const name of (parsed.keepTags || [])) {
        const existing = existingTags.find(t => t.name === name);
        tags.push({
            name: name.trim(),
            isActive: true,
            isNew: false,
            createdAt: existing?.createdAt || now,
        });
    }

    // 제거 태그 (비활성, 취소선용)
    for (const name of (parsed.removeTags || [])) {
        tags.push({
            name: name.trim(),
            isActive: false,
            isNew: false,
            createdAt: now,
        });
    }

    // 신규 태그
    for (const name of (parsed.newTags || [])) {
        tags.push({
            name: name.trim(),
            isActive: true,
            isNew: true,
            createdAt: now,
        });
    }

    return {
        tags,
        category: parsed.category,
        summary: parsed.summary || '',
    };
}

// ---------------------------------------------------------------------------
// Category Emoji — AI가 카테고리 이름에 적합한 이모지 선택
// ---------------------------------------------------------------------------

export async function pickCategoryEmoji(categoryName: string): Promise<string> {
    const model = getModel();
    const prompt = `당신은 이모지 전문가입니다. 아래 카테고리 이름에 가장 잘 어울리는 이모지 1개만 응답하세요.
규칙:
- 이모지 딱 1개만 응답 (텍스트 없이)
- 흔하고 모든 플랫폼에서 잘 표시되는 이모지 사용

카테고리: "${categoryName}"`;

    try {
        const result = await model.generateContent(prompt);
        const emoji = result.response.text().trim();
        // 이모지가 아닌 경우 기본값
        if (!emoji || emoji.length > 4) return '📌';
        return emoji;
    } catch (e) {
        console.warn('[AI] pickCategoryEmoji failed:', e);
        return '📌';
    }
}

// ---------------------------------------------------------------------------
// 5. polishDictation — 음성 받아쓰기 최소 교정
// ---------------------------------------------------------------------------

/**
 * 음성인식으로 받아적은 텍스트를 AI가 최소한으로 교정합니다.
 * - 발음 뭉개짐으로 인한 오탈자 보정
 * - 다시 말하며 중복된 내용 제거
 * - 문맥/전체 맥락을 통한 자연스러운 어투 교정
 * - 원문의 의미와 표현을 최대한 보존
 */
export async function polishDictation(
    rawText: string,
    language?: string,
): Promise<string> {
    if (!rawText.trim()) return rawText;

    const lang = language || 'ko';
    const model = getModel();

    const prompt = `You are a dictation assistant. The user recorded speech and it was transcribed by speech recognition.
Your job is to make MINIMAL corrections to make the text natural and clear.

**Rules:**
1. PRESERVE the original meaning and expression as much as possible.
2. Fix obvious speech recognition errors (garbled pronunciation, wrong words).
3. Remove duplicated phrases caused by the speaker re-saying something.
4. Make minor grammar/spelling corrections for natural readability.
5. Do NOT rewrite, summarize, or change the tone. Keep the speaker's voice.
6. Do NOT add any content that was not in the original.
7. If the text is already fine, return it as-is.

**Language:** ${langLabel(lang)}

**Raw transcription:**
${rawText}

**IMPORTANT:** Return ONLY the corrected text. No explanations, no JSON, no markdown. Just the polished text.`;

    try {
        const result = await model.generateContent(prompt);
        const polished = result.response.text().trim();
        return polished || rawText;
    } catch (e) {
        console.warn('[AI] polishDictation failed:', e);
        return rawText;
    }
}

// ---------------------------------------------------------------------------
// 6. askAI — 범용 텍스트 프롬프트 (번역, 요약, 텍스트 어시스턴트)
// ---------------------------------------------------------------------------

/**
 * 범용 텍스트 프롬프트 (서버 경유).
 * 기존 서버사이드 OpenAI `askChatGPT`를 대체합니다.
 * @param temperature 0이면 결정적 응답(번역 등), 생략 시 모델 기본값
 */
export async function askAI(prompt: string, temperature?: number): Promise<string> {
    const model = getAiTextModel(MODEL_NAME, temperature);
    try {
        const result = await model.generateContent(prompt);
        return result.response.text().trim();
    } catch (e) {
        console.error('[AI] askAI failed:', e);
        throw e;
    }
}

